"use client";

import React from "react";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { motion } from "framer-motion";

import { PageMentoringV2Schema } from "@/sanity/pages/PageMentoringV2Template";
import rounded from "@/public/global/rounded-corner.svg";
import Image from "next/image";

const fadeInUp = (delay?: number) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
});

export default function BookingSection({ data, className }: { data: PageMentoringV2Schema; className?: string }) {
  return (
    <section className={cn("relative overflow-clip bg-[#0A1A1D] py-[8rem] lg:py-[16rem]", className)}>
      <Image
        src={rounded}
        alt=""
        className="hidden absolute right-0 bottom-0 lg:block"
      />
      <DecorationLine
        className="hidden top-0 lg:block"
        orientation="vertical"
        position="left"
        style="light"
      />
      <DecorationLine
        className="hidden top-0 lg:block"
        orientation="vertical"
        position="right"
        style="light"
      />

      <div className="container flex flex-col items-center">
        <div className="w-[min(186rem,100%)] flex flex-col lg:flex-row items-center lg:items-start gap-[4rem] lg:gap-[8rem] justify-between lg:h-[49.125rem]">
          {/* Left: Chip + Text */}
          <div className="w-[min(73.625rem,100%)] flex flex-col items-start">
            {data?.bookingChip && (
              <motion.div {...fadeInUp()}>
                <Chip
                  image={data.bookingChip.icon}
                  text={data.bookingChip.text}
                />
              </motion.div>
            )}

            {data?.bookingText && (
              <motion.div
                {...fadeInUp(0.1)}
                className="richtext richtext-start prose-p:text-white/80 prose-headings:text-white prose-headings:font-ivy-headline-regular prose-headings:tracking-[0.075rem] prose-headings:text-[5rem] lg:prose-headings:text-[7rem]">
                <PortableText
                  value={data.bookingText}
                  components={richTextComponent}
                />
              </motion.div>
            )}
          </div>

          {/* Right: Callout box */}
          {data?.bookingCallout && (
            <motion.div
              {...fadeInUp(0.15)}
              className="w-[min(91.875rem,100%)] bg-[#D9F9B9] p-[4rem] lg:p-[6rem] flex flex-col items-start [clip-path:polygon(0_0,100%_0,100%_calc(100%-4rem),calc(100%-4rem)_100%,0_100%)] lg:[clip-path:polygon(0_0,100%_0,100%_calc(100%-6rem),calc(100%-6rem)_100%,0_100%)] h-full prose-headings:font-ivy-headline-regular">
              {data.bookingCallout.text && (
                <div className="richtext richtext-start prose-p:text-[#0A1A1D]/80 prose-p:w-[min(71.5rem,100%)] prose-em:font-ivy prose-em:tracking-[0.3px] prose-em:font-light prose-headings:text-[#0A1A1D] lg:prose-headings:text-[7rem] prose-headings:text-[4rem]">
                  <PortableText
                    value={data.bookingCallout.text}
                    components={richTextComponent}
                  />
                </div>
              )}

              {data.bookingCallout.cta && (
                <CTACornerButton
                  className="mt-[4rem]"
                  data={data.bookingCallout.cta}
                />
              )}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
